// The layout every outgoing email is sent in, shared so the worker sends exactly what the admin
// Notifications page previews. It wears the app's own look (DESIGN.md): the deep-indigo chrome band with
// the wave mark and the ROLODEX wordmark, the indigo accent, near-square corners, sentence-case labels,
// Archivo titles over Plex text (system fonts where an email client won't load them).
//
// An email is written as plain text (a default template, an admin's custom one, or a message the service
// team typed) and this turns it into email-safe HTML:
//   • a blank line starts a new paragraph, a single line break stays a line break;
//   • two or more "Label: value" lines in a row become a details table (SAP, Model, Layout…);
//   • lines starting with "•" or "-" become a list, and in a list
//       "Engine: 2x300 → 2x350" shows the old value struck through beside the new one,
//       "Added: Teak deck" / "Removed: …" get a small green / red tag;
//   • a short line ending in ":" heads what follows it ("What changed (2):" over a list), and over plain
//     text it frames that text as a quote ("What they need:" over the client's words);
//   • web addresses become links.
// Around it: a small label naming what it's about, the title, an optional button into the app, and a
// footer that says why you got it. Clients get a Technohull letterhead instead, with no app name, and a
// footer inviting replies. Table-based with inline styles, so it holds up in Gmail, Outlook and Apple
// Mail; the plain-text part carries the same words for clients without HTML.
// the indigo default profile (DESIGN.md tokens), mixed down to plain hex for email clients
const C = {
    chrome: '#12132e', // the band
    onChrome: '#ffffff',
    onChrome2: '#b8b9d6', // the org line under the wordmark
    accent: '#4b4e8f', // label, links, button
    accentDim: '#383a70',
    ink: '#1c1b2b', // text
    soft: '#3d3c52', // list text
    muted: '#6a6980', // labels, footer
    canvas: '#ecedf5', // around the card
    card: '#ffffff',
    sunken: '#f4f4f9', // quotes
    line: '#d9dae6',
    hair: '#e7e7f0',
    okBg: '#e6f0e9', okFg: '#2c6a44', okBd: '#c2dbcb',
    errBg: '#f7e8e9', errFg: '#a13838', errBd: '#e8c4c6',
};
const TITLE_FONT = "Archivo,'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const TEXT_FONT = "'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const MARK_FONT = "Rajdhani,Archivo,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const FONTS_CSS = 'https://fonts.googleapis.com/css2?family=Archivo:wght@700;800&family=IBM+Plex+Sans:wght@400;600&family=Rajdhani:wght@700&display=swap';
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** escape, then turn web addresses into links. */
function inline(s) {
    return esc(s).replace(/\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/g, (u) => `<a href="${u}" style="color:${C.accent};text-decoration:underline;">${u}</a>`);
}
/** "Rolodex: Order 12 confirmed" → "Order 12 confirmed" (the band already says who it's from). */
export function emailTitle(subject) {
    return (subject || '').replace(/^\s*(?:\[test\]\s*)?rolodex\s*[:—–-]\s*/i, '').replace(/^\s*\[test\]\s*/i, '').trim() || 'Notification';
}
const BULLET = /^\s*[•\-*]\s+(.*\S)\s*$/;
// a label of up to four words (a field, or a person's name or address) before the colon
const DETAIL = /^([A-Za-z\u0370-\u03ff\u1f00-\u1fff0-9@][^:\n]{0,39}):\s+(\S.*)$/;
const isDetail = (line) => {
    const m = DETAIL.exec(line);
    return !!m && !/https?$/i.test(m[1]) && m[1].trim().split(/\s+/).length <= 4;
};
const CHANGE = /^(.+?):\s*(.*?)\s*(?:→|->)\s*(.*)$/;
const TAGGED = /^(Added|Removed)(\s*\(custom\))?:\s*(.+)$/i;
// a value that says something is missing reads quieter ("did not check in", "none", "-")
const QUIET = /^(?:did not|not\b|none\b|no\b|-$|—$)/i;
// a short line ending in ":" that heads what follows it
const isHeading = (line) => line.trim().endsWith(':') && line.trim().length <= 40;
/** a block (no blank lines) as runs of plain lines, details and bullets. A lone "Label: value" line
 *  reads as a sentence, so only two or more in a row make a table. */
function runsOf(block) {
    const runs = [];
    for (const line of block.split('\n')) {
        const kind = BULLET.test(line) ? 'bullet' : isDetail(line) ? 'detail' : 'text';
        const last = runs[runs.length - 1];
        if (last && last.kind === kind)
            last.lines.push(line);
        else
            runs.push({ kind, lines: [line] });
    }
    const merged = [];
    for (const r of runs) {
        const kind = r.kind === 'detail' && r.lines.length < 2 ? 'text' : r.kind;
        const last = merged[merged.length - 1];
        if (last && last.kind === 'text' && kind === 'text')
            last.lines.push(...r.lines);
        else
            merged.push({ kind, lines: [...r.lines] });
    }
    return merged;
}
const blocksOf = (text) => (text || '').replace(/\r\n/g, '\n').trim().split(/\n\s*\n/).map((b) => b.replace(/\s+$/g, '')).filter((b) => b.trim());
const para = (lines) => `<p style="margin:0 0 14px;color:${C.ink};font-family:${TEXT_FONT};font-size:15px;line-height:1.6;">${lines.map(inline).join('<br>')}</p>`;
const heading = (line) => `<p style="margin:20px 0 8px;color:${C.muted};font-family:${TEXT_FONT};font-size:13px;font-weight:600;line-height:1.3;">${esc(line.trim().replace(/:$/, ''))}</p>`;
const quote = (lines) => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;"><tr>`
    + `<td style="background:${C.sunken};border-left:3px solid ${C.accent};border-radius:2px;padding:12px 16px;color:${C.ink};font-family:${TEXT_FONT};font-size:14.5px;line-height:1.6;">${lines.map(inline).join('<br>')}</td></tr></table>`;
function tag(word, custom) {
    const added = /^added$/i.test(word);
    const [bg, fg, bd] = added ? [C.okBg, C.okFg, C.okBd] : [C.errBg, C.errFg, C.errBd];
    return `<span style="display:inline-block;padding:1px 7px;border:1px solid ${bd};border-radius:2px;background:${bg};color:${fg};font-size:12px;font-weight:600;line-height:1.5;">${added ? 'Added' : 'Removed'}${custom ? ' (custom)' : ''}</span>`;
}
/** one list item: a change ("Engine: old → new"), an added/removed item, or plain text. */
function listItem(text) {
    const t = TAGGED.exec(text);
    if (t)
        return `${tag(t[1], !!t[2])}&nbsp; ${inline(t[3])}`;
    const c = CHANGE.exec(text);
    if (c) {
        const from = c[2].trim();
        const to = c[3].trim();
        return `<span style="color:${C.ink};font-weight:600;">${esc(c[1].trim())}</span>`
            + `<span style="color:${C.muted};">: </span>`
            + (from && from !== '-' ? `<span style="color:${C.muted};text-decoration:line-through;">${inline(from)}</span> <span style="color:${C.muted};">→</span> ` : '')
            + `<span style="color:${C.ink};font-weight:600;">${inline(to || '-')}</span>`;
    }
    return inline(text);
}
function list(lines) {
    const rows = lines.map((l, i) => {
        const text = BULLET.exec(l)[1];
        const top = i ? `border-top:1px solid ${C.hair};` : '';
        return `<tr><td style="${top}padding:9px 0 9px 2px;width:14px;vertical-align:top;color:${C.accent};font-size:14px;line-height:1.5;">•</td>`
            + `<td style="${top}padding:9px 0;color:${C.soft};font-family:${TEXT_FONT};font-size:14.5px;line-height:1.5;">${listItem(text)}</td></tr>`;
    }).join('');
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">${rows}</table>`;
}
function facts(lines) {
    const rows = lines.map((l, i) => {
        const m = DETAIL.exec(l);
        const value = m[2].trim();
        const quiet = QUIET.test(value);
        const top = i ? `border-top:1px solid ${C.hair};` : '';
        return `<tr><td style="${top}padding:9px 14px;color:${C.muted};font-family:${TEXT_FONT};font-size:13px;line-height:1.45;width:34%;vertical-align:top;">${esc(m[1].trim())}</td>`
            + `<td style="${top}padding:9px 14px;color:${quiet ? C.muted : C.ink};font-family:${TEXT_FONT};font-size:14px;font-weight:${quiet ? 400 : 600};line-height:1.45;vertical-align:top;">${inline(value)}</td></tr>`;
    }).join('');
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;border:1px solid ${C.line};border-radius:4px;border-collapse:separate;">${rows}</table>`;
}
function bodyHtml(text) {
    const out = [];
    for (const block of blocksOf(text)) {
        const runs = runsOf(block);
        runs.forEach((run, i) => {
            if (run.kind === 'bullet') {
                out.push(list(run.lines));
                return;
            }
            if (run.kind === 'detail') {
                out.push(facts(run.lines));
                return;
            }
            const lines = run.lines;
            const last = lines[lines.length - 1];
            // "What changed (2):" over a list or table
            if (runs[i + 1] && isHeading(last)) {
                if (lines.length > 1)
                    out.push(para(lines.slice(0, -1)));
                out.push(heading(last));
                return;
            }
            // "What they need:" over the client's own words
            if (lines.length >= 2 && isHeading(lines[0])) {
                out.push(heading(lines[0]), quote(lines.slice(1)));
                return;
            }
            out.push(para(lines));
        });
    }
    return out.join('');
}
/** the first sentence-ish line, for the inbox preview (preheader). */
function preheader(text) {
    const first = blocksOf(text)[0]?.split('\n')[0] ?? '';
    return first.replace(BULLET, '$1').slice(0, 140);
}
function footer(c) {
    const p = (s) => `<p style="margin:0 0 6px;color:${C.muted};font-family:${TEXT_FONT};font-size:12px;line-height:1.55;">${s}</p>`;
    if (c.audience === 'client') {
        // the message is signed by the team itself; the footer only says how to answer
        const reply = c.replyTo
            ? ` or write to <a href="mailto:${esc(c.replyTo)}" style="color:${C.accent};text-decoration:underline;">${esc(c.replyTo)}</a>`
            : '';
        const html = p(`Questions about your request? Reply to this email${reply}.`);
        return { html, text: `Questions about your request? Reply to this email${c.replyTo ? ` or write to ${c.replyTo}` : ''}.` };
    }
    const why = c.reason ? `${c.reason} ` : '';
    const note = "This is an automated message from Rolodex, so replies aren't read.";
    return { html: p(esc(why + note)), text: why + note };
}
/** the band: the wave mark and the wordmark over its org line (ROLODEX / TECHNOHULL for staff; clients
 *  only ever see Technohull, so theirs reads TECHNOHULL / SERVICE). */
function band(c, logoSrc) {
    const [mark, org] = c.audience === 'client' ? ['TECHNOHULL', 'SERVICE'] : ['ROLODEX', 'TECHNOHULL'];
    return `<td style="background:${C.chrome};padding:18px 28px;">
          <table role="presentation" cellpadding="0" cellspacing="0"><tr>
            <td style="vertical-align:middle;padding-right:12px;"><img src="${esc(logoSrc)}" width="56" alt="" style="display:block;width:56px;height:auto;border:0;outline:none;text-decoration:none;"></td>
            <td style="vertical-align:middle;border-left:1px solid #2c2d4c;padding-left:12px;">
              <div style="color:${C.onChrome};font-family:${MARK_FONT};font-size:19px;font-weight:700;letter-spacing:.12em;line-height:1;">${mark}</div>
              <div style="margin-top:4px;color:${C.onChrome2};font-family:${TEXT_FONT};font-size:9.5px;font-weight:600;letter-spacing:.22em;line-height:1;">${org}</div>
            </td>
          </tr></table>
        </td>`;
}
/** the email as HTML. `logoSrc` is `cid:…` when sending (inline attachment) or a data: URI for a preview;
 *  `webFonts: false` leaves out the Google Fonts link (a preview inside the app, under its own CSP). */
export function renderEmailHtml(c, logoSrc, opts = {}) {
    const title = esc(emailTitle(c.subject));
    const kicker = c.kicker
        ? `<div style="margin:0 0 8px;color:${C.accent};font-family:${TEXT_FONT};font-size:13px;font-weight:600;line-height:1.2;">${esc(c.kicker)}</div>`
        : '';
    const button = c.action
        ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:10px 0 4px;"><tr><td style="border-radius:4px;background:${C.accent};">`
            + `<a href="${esc(c.action.url)}" style="display:inline-block;padding:11px 20px;font-family:${TEXT_FONT};font-size:14px;font-weight:600;line-height:1.2;color:#ffffff;text-decoration:none;border-radius:4px;">${esc(c.action.label)}&nbsp;&nbsp;→</a>`
            + `</td></tr></table>`
        : '';
    const foot = footer(c);
    const fonts = opts.webFonts === false ? '' : `\n<link rel="stylesheet" href="${FONTS_CSS}">`;
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light only">
<meta name="supported-color-schemes" content="light">
<title>${title}</title>${fonts}
<style>
  a { color: ${C.accent}; }
  @media (max-width: 620px) {
    .em-card { width: 100% !important; }
    .em-pad { padding-left: 20px !important; padding-right: 20px !important; }
    .em-outer { padding: 10px 6px !important; }
    .em-title { font-size: 20px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:${C.canvas};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${esc(preheader(c.body))}</div>
<table role="presentation" class="em-outer" width="100%" cellpadding="0" cellspacing="0" style="background:${C.canvas};padding:28px 12px;">
  <tr><td align="center">
    <table role="presentation" class="em-card" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background:${C.card};border:1px solid ${C.line};border-radius:4px;border-collapse:separate;overflow:hidden;box-shadow:0 1px 2px rgba(18,19,46,.06);">
      <tr>
        ${band(c, logoSrc)}
      </tr>
      <tr>
        <td class="em-pad" style="padding:28px 32px 8px;">
          ${kicker}<h1 class="em-title" style="margin:0 0 18px;color:${C.ink};font-family:${TITLE_FONT};font-size:22px;line-height:1.25;font-weight:700;letter-spacing:-.01em;">${title}</h1>
          ${bodyHtml(c.body)}
          ${button}
        </td>
      </tr>
      <tr>
        <td class="em-pad" style="padding:18px 32px 24px;">
          <div style="border-top:1px solid ${C.hair};padding-top:14px;">${foot.html}</div>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;
}
/** the same email as plain text (the multipart fallback). */
export function renderEmailText(c) {
    const parts = [(c.body || '').replace(/\r\n/g, '\n').trim()];
    if (c.action)
        parts.push(`${c.action.label}: ${c.action.url}`);
    parts.push(`--\n${footer(c).text}`);
    return parts.join('\n\n');
}
//# sourceMappingURL=emailLayout.js.map